import React, { useEffect, useState } from 'react';

/**
 * Phase 28 — Live chat overlay (Instagram-style).
 *
 * Renders the last N messages, fading out after `fadeMs`. Designed to sit
 * absolutely positioned inside the LiveKit video container so the creator
 * can read while looking at the camera.
 *
 * Props:
 *   • messages        — full message array (newest at end)
 *   • visibleCount    — how many messages to keep on screen (default 5)
 *   • fadeMs          — fade-out delay in milliseconds (default 8000)
 *   • blockedIds      — set of user IDs whose messages are hidden client-side
 */
const LiveChatOverlay = ({
  messages = [],
  visibleCount = 5,
  fadeMs = 8000,
  blockedIds = null,
}) => {
  const [, setTick] = useState(0);

  // Trigger a re-render every second so fade-out timestamps update
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const now = Date.now();
  const filtered = messages.filter((m) => {
    if (blockedIds && blockedIds.has?.(m.author?.id)) return false;
    return true;
  });
  const visible = filtered.slice(-visibleCount).map((m) => {
    const age = now - (m.ts || now);
    const opacity = age > fadeMs ? 0 : age > fadeMs - 1500 ? Math.max(0, 1 - (age - (fadeMs - 1500)) / 1500) : 1;
    return { ...m, opacity };
  }).filter((m) => m.opacity > 0);

  if (visible.length === 0) return null;

  return (
    <div
      className="pointer-events-none absolute bottom-3 left-3 right-3 flex flex-col-reverse gap-2 sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-sm"
      style={{
        // Phase B37.H · P1 — Safe area iPhone: la chat non finisce sotto
        // la home-indicator.
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0px)',
        maxWidth: 'min(85%, 24rem)',
      }}
      data-testid="live-chat-overlay"
    >
      {visible.slice().reverse().map((m) => (
        <div
          key={m.id}
          className={`rounded-2xl bg-black/55 backdrop-blur-md px-3 py-1.5 text-white shadow-md transition-opacity duration-700 ${
            m.type === 'highlighted' ? 'ring-1 ring-amber-400/60' : ''
          } ${m.type === 'tip' ? 'bg-gradient-to-r from-amber-500/70 to-amber-700/70' : ''}`}
          style={{ opacity: m.opacity }}
          data-testid={`live-chat-overlay-msg-${m.id}`}
        >
          <div className="flex items-baseline gap-2">
            <span className={`text-xs font-semibold ${m.author?.is_creator ? 'text-amber-300' : 'text-amber-200/90'}`}>
              {m.author?.username || 'guest'}
              {m.author?.is_creator && ' ✦'}
            </span>
          </div>
          <p className="text-sm leading-snug break-words">{m.body}</p>
        </div>
      ))}
    </div>
  );
};

export default LiveChatOverlay;
